/**
 * Fifty Rooms — cart additions (drawer + /cart). Loaded from snippets/cart-drawer.liquid and sections/main-cart.liquid.
 *
 * <fr-cart-upsell> ("Complete the set", snippets/fr-cart-upsell.liquid): the Add button posts ONE /cart/add.js
 * (JSON, with `sections` = every cart-items-component section) and, after a successful add, dispatches Horizon's
 * CartLinesUpdateEvent with the result, so Horizon morphs the cart drawer / cart page, the header count and everything that depends on the
 * cart (free-shipping bar, bundle hint, totals — all plain Liquid in those sections) without a page reload.
 *
 * Also keeps the mobile /cart summary compact: only totals + Checkout stay pinned to the bottom of the viewport
 * (CSS variable --fr-cart-below = height of everything under the Checkout button, see fr-cart.css).
 */

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** @type {Promise<any> | null} */
let eventsModule = null;
function loadEvents() {
  if (!eventsModule) eventsModule = import('@shopify/events').catch(() => ({}));
  return eventsModule;
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter((id) => !!id);
}

class FrCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {string} message */
  #showError(message) {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('[data-fr-upsell-error]'));
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-fr-upsell-add]') || null);
    if (!button || button.disabled || !this.contains(button)) return;
    event.preventDefault();
    this.#add(button);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const id = Number(button.dataset.variantId);
    if (!id) return;
    /** @type {{id: number, quantity: number, properties?: Record<string, string>}} */
    const item = { id, quantity: 1 };
    if (button.dataset.set) item.properties = { _set: button.dataset.set };
    const sectionIds = cartSectionIds();

    this.#showError('');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('fr-is-loading');

    try {
      /** @type {Record<string, unknown>} */
      const body = { items: [item] };
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      /** @type {any} */
      let json = {};
      try {
        json = await response.json();
      } catch {}
      if (!response.ok || json.status) {
        // Nothing was added: no cart event (a re-render would wipe this message), the button is released below.
        this.#showError(json.description || json.message || this.dataset.error || '');
        return;
      }
      const cart = await fetch(`${ROOT}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
        (r) => r.json()
      );

      // The cart changed: tell Horizon (cart-items-component morphs the sections, header count, drawer state).
      const events = await loadEvents();
      const CartEvent = events.CartLinesUpdateEvent;
      if (!CartEvent) {
        // @shopify/events did not load: nothing listens, so reload to show the new cart.
        window.location.reload();
        return;
      }
      this.dispatchEvent(
        new CartEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: String(id), quantity: 1 }],
          promise: Promise.resolve({
            cart: CartEvent.createCartFromAjaxResponse ? CartEvent.createCartFromAjaxResponse(cart) : cart,
            detail: {
              items: cart.items,
              source: 'fr-cart-upsell',
              sourceId: 'fr-cart-upsell',
              itemCount: 1,
              sections: json.sections || undefined,
              didError: false,
            },
          }),
        })
      );
    } catch (error) {
      this.#showError(this.dataset.error || '');
    } finally {
      // Always release the button: after a successful add the row of the added product is removed by the re-render,
      // but the morph may reuse this very node for the next candidate — it must never stay disabled.
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('fr-is-loading');
    }
  }
}

if (!customElements.get('fr-cart-upsell')) customElements.define('fr-cart-upsell', FrCartUpsell);

/* ---------------------------------------------- mobile /cart: compact pinned summary */

/** @type {ResizeObserver | null} */
let belowObserver = null;
/** @type {WeakSet<Element>} */
const observed = new WeakSet();

/** Height of everything below the Checkout button inside the /cart summary → --fr-cart-below on the summary. */
function measureBelow() {
  const summary = /** @type {HTMLElement | null} */ (document.querySelector('.cart-page__summary'));
  const button = summary?.querySelector('#checkout');
  if (!summary || !button) return;
  const below = Math.max(0, Math.round(summary.getBoundingClientRect().bottom - button.getBoundingClientRect().bottom) - 12);
  if (summary.style.getPropertyValue('--fr-cart-below') !== `${below}px`) {
    summary.style.setProperty('--fr-cart-below', `${below}px`);
  }
  if (!belowObserver && 'ResizeObserver' in window) {
    belowObserver = new ResizeObserver(() => requestAnimationFrame(measureBelow));
  }
  for (const el of [summary, summary.querySelector('.cart__ctas')]) {
    if (!el || observed.has(el)) continue;
    observed.add(el);
    belowObserver?.observe(el);
  }
}

function initBelow() {
  const page = document.querySelector('.cart-page');
  if (!page) return;
  measureBelow();
  // The summary is morphed after every cart change (and the express buttons load late): measure again.
  new MutationObserver(() => requestAnimationFrame(measureBelow)).observe(page.closest('.shopify-section') || document.body, {
    childList: true,
    subtree: true,
  });
  window.addEventListener('resize', () => requestAnimationFrame(measureBelow));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initBelow, { once: true });
else initBelow();
